import { authToken } from "@infrastructure/clients/http/authToken";
import { BASE_URL } from "@infrastructure/clients/http/config";

const TICKET_KEY = "demo_ticket";

/**
 * Вход за участника показа.
 *
 * Приложение обычно узнаёт человека по подписи мессенджера. На презентации это
 * мешает: чтобы показать программу с разных сторон, пришлось бы держать десяток
 * телефонов. Ссылка с пропуском позволяет открыть приложение за нужного человека
 * в обычном браузере, в отдельной вкладке на каждого.
 *
 * В ссылке едет пропуск, а не токен. Токен живёт полчаса, и ссылки, розданные до
 * начала презентации, протухли бы к её началу. Пропуск не истекает: приложение
 * меняет его на свежий вход при каждом открытии — ровно так же, как в мессенджере
 * меняет на вход его подпись.
 *
 * Пропуск остаётся в хранилище, поэтому вкладку можно перезагружать сколько
 * угодно, а из адреса он убирается сразу — незачем ему оставаться в истории
 * браузера.
 *
 * Сервер отдаёт вход только за подставного человека из демо-диапазона; за
 * настоящего так войти нельзя.
 */
/** Идёт ли показ: человек вошёл по пропуску, а не подписью мессенджера. */
export function isDemoSession(): boolean {
  return Boolean(localStorage.getItem(TICKET_KEY));
}

export function hasDemoTicket(): boolean {
  return Boolean(ticketInUrl() || localStorage.getItem(TICKET_KEY));
}

export async function applyDemoLoginFromUrl(): Promise<boolean> {
  const ticket = takeTicketFromUrl() || localStorage.getItem(TICKET_KEY);
  if (!ticket) {
    return false;
  }

  try {
    const response = await fetch(`${BASE_URL}/api/auth/demo/signin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ticket }),
    });
    const body = await response.json();
    const token = body?.data?.token;
    if (!token) {
      // Пропуск убираем только если сервер прямо отказал: при обрыве связи он
      // ещё пригодится, а без него вкладку было бы не восстановить.
      if (response.status === 401 || response.status === 400) {
        localStorage.removeItem(TICKET_KEY);
      }
      return false;
    }

    localStorage.setItem(TICKET_KEY, ticket);
    authToken.set(token);
    return true;
  } catch {
    // Сеть отвалилась — прошлый токен ещё может быть жив, вкладку не ломаем.
    return false;
  }
}

/** Пропуск в адресе — до того, как он оттуда убран. */
function ticketInUrl(): string | null {
  const url = new URL(window.location.href);

  // Приложение живёт на адресах с решёткой, и параметр может оказаться в хвосте.
  const hashQuery = window.location.hash.includes("?")
    ? new URLSearchParams(window.location.hash.split("?")[1])
    : null;

  return url.searchParams.get("demo") || hashQuery?.get("demo") || null;
}

/** Забрать пропуск из адреса и убрать его оттуда. */
function takeTicketFromUrl(): string | null {
  const ticket = ticketInUrl();
  if (!ticket) {
    return null;
  }

  const url = new URL(window.location.href);
  const hashQuery = window.location.hash.includes("?")
    ? new URLSearchParams(window.location.hash.split("?")[1])
    : null;

  url.searchParams.delete("demo");
  if (hashQuery) {
    hashQuery.delete("demo");
    const base = window.location.hash.split("?")[0];
    const rest = hashQuery.toString();
    url.hash = rest ? `${base}?${rest}` : base;
  }
  window.history.replaceState({}, "", url.toString());

  return ticket;
}
